// ═══════════════════════════════════════════════════════════════════════════════
// Generate Pairing Code Page (Employer Only)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/ToastProvider';
import { AppButton } from '../../components/ui/AppButton';
import { NotificationBell } from '../../components/NotificationBell';
import { MainMenuButton } from '../../components/MainMenuButton';
import { getPostAuthHomePath } from '../../lib/authRedirect';
import { reconcilePairedUserProfile } from '../../lib/reconcileUserPairing';
import { buildPairInviteUrl } from '../../lib/pairInviteSession';
import {
  formatPairingExpiryDurationLabel,
  isPairingCodeNotExpired,
} from '../../lib/pairingCodeExpiry';
import { PAIRING_CODE_EXPIRY_MINUTES } from '@fdh/shared';
import type { ActivePairingCodeResponse, GeneratePairingCodeResponse } from '@fdh/shared';
import { MainAppShell } from '../../components/layout/MainAppShell';
import { PLAY_BANNER_ABSOLUTE_TOP_4_CLASS } from '../../lib/layoutChrome';
import {
  PairingCopyCheckIcon,
  PairingCopyClipboardIcon,
} from '../../components/pairing/PairingCopyIcons';
import { PairingInviteQrCode } from '../../components/pairing/PairingInviteQrCode';

const isPairedProfile = (pairedAt: string | undefined): boolean =>
  Boolean(pairedAt && pairedAt.trim() !== '');

const hasConsented = (consentedAt: string | undefined): boolean =>
  Boolean(consentedAt && consentedAt.trim() !== '');

export const GenerateCodePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { refreshUser, updateUser, user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const { showSuccessToast } = useToast();
  const tRef = useRef(t);
  tRef.current = t;
  const showSuccessToastRef = useRef(showSuccessToast);
  showSuccessToastRef.current = showSuccessToast;
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleNavigateIfPairedAfterRefresh = useCallback(async (): Promise<boolean> => {
    const currentUser = userRef.current;
    if (!currentUser) {
      return false;
    }
    const reconciled = await reconcilePairedUserProfile(
      refreshUser,
      updateUser,
      currentUser
    );
    if (reconciled && isPairedProfile(reconciled.pairedAt)) {
      navigate(getPostAuthHomePath(reconciled), { replace: true });
      return true;
    }
    return false;
  }, [navigate, refreshUser, updateUser]);

  const handleGenerateCode = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setCopied(false);

    try {
      const response = await api.post<GeneratePairingCodeResponse>(
        '/pairing/generate-code'
      );

      if (!response.success || !response.data) {
        throw new Error(response.error ?? t('pairing.generate.errorGeneric'));
      }

      setCode(response.data.code);
      setExpiresAt(response.data.expiresAt);
      showSuccessToast(t('toast.pairing.generateCodeSuccess'));
    } catch (err) {
      const isAlreadyPairedConflict =
        err instanceof ApiClientError && err.status === 409;
      if (isAlreadyPairedConflict) {
        const redirected = await handleNavigateIfPairedAfterRefresh();
        if (redirected) {
          return;
        }
      }
      setError(err instanceof Error ? err.message : t('pairing.generate.errorGeneric'));
    } finally {
      setIsLoading(false);
    }
  }, [handleNavigateIfPairedAfterRefresh, showSuccessToast, t]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    const cancelled = { current: false };

    void (async () => {
      const currentUser = userRef.current;
      if (!currentUser) {
        return;
      }

      const reconciled = await reconcilePairedUserProfile(
        refreshUser,
        updateUser,
        currentUser
      );
      if (cancelled.current) {
        return;
      }
      if (reconciled && isPairedProfile(reconciled.pairedAt)) {
        navigate(getPostAuthHomePath(reconciled), { replace: true });
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const activeResponse = await api.get<ActivePairingCodeResponse>(
          '/pairing/active-code'
        );
        if (cancelled.current) {
          return;
        }

        const activeCode = activeResponse.data?.code;
        const activeExpiresAt = activeResponse.data?.expiresAt;
        if (
          activeResponse.success &&
          activeCode &&
          activeExpiresAt &&
          isPairingCodeNotExpired(activeExpiresAt)
        ) {
          setCode(activeCode);
          setExpiresAt(activeExpiresAt);
          return;
        }

        const generateResponse = await api.post<GeneratePairingCodeResponse>(
          '/pairing/generate-code'
        );
        if (cancelled.current) {
          return;
        }

        if (!generateResponse.success || !generateResponse.data) {
          throw new Error(
            generateResponse.error ?? tRef.current('pairing.generate.errorGeneric')
          );
        }

        setCode(generateResponse.data.code);
        setExpiresAt(generateResponse.data.expiresAt);
        showSuccessToastRef.current(tRef.current('toast.pairing.generateCodeSuccess'));
      } catch (err) {
        if (cancelled.current) {
          return;
        }
        const isAlreadyPairedConflict =
          err instanceof ApiClientError && err.status === 409;
        if (isAlreadyPairedConflict) {
          const redirected = await handleNavigateIfPairedAfterRefresh();
          if (redirected) {
            return;
          }
        }
        setError(
          err instanceof Error
            ? err.message
            : tRef.current('pairing.generate.errorGeneric')
        );
      } finally {
        if (!cancelled.current) {
          setIsLoading(false);
          setIsBootstrapping(false);
        }
      }
    })();

    return () => {
      cancelled.current = true;
    };
  }, [user?.id, navigate, refreshUser, updateUser, handleNavigateIfPairedAfterRefresh]);

  const handleCopyCode = useCallback(async () => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [code]);

  const handleCopyInviteLink = useCallback(async () => {
    if (!code) return;
    const url = buildPairInviteUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [code]);

  const handleContinue = useCallback(() => {
    navigate('/pairing/status');
  }, [navigate]);

  const codeStillValid = Boolean(code) && isPairingCodeNotExpired(expiresAt);
  const showMainShell = Boolean(user?.emailVerified) && hasConsented(user?.consentedAt);

  const content = (
    <div className={`relative flex items-center justify-center bg-gray-50 px-4 dark:bg-gray-950 ${showMainShell ? 'min-h-full py-6' : 'min-h-screen'}`}>
      {showMainShell ? null : (
        <div className={`absolute right-4 z-10 flex items-center gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
          <MainMenuButton />
          <NotificationBell />
        </div>
      )}
      <div className={`w-full ${showMainShell ? 'max-w-lg' : 'max-w-md'}`}>
        <div className="rounded-2xl bg-white p-8 shadow-lg dark:bg-gray-900 dark:shadow-gray-950/50">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
              <svg
                className="h-8 w-8 text-primary-600 dark:text-primary-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
            </div>
            <h1
              className="text-2xl font-bold text-gray-900 dark:text-white"
              role="heading"
              aria-level={1}
            >
              {t('pairing.generate.title')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('pairing.generate.subtitle')}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Code Display */}
          {code ? (
            <div className="mb-6">
              <div className="rounded-xl border border-primary-200 bg-primary-50 p-6 text-center dark:border-primary-600 dark:bg-gray-800">
                <p className="mb-2 text-xs font-medium tracking-wider text-primary-700 uppercase dark:text-primary-200">
                  {t('pairing.generate.codeLabel')}
                </p>
                <div className="flex min-w-0 items-center justify-center gap-2 px-1">
                  <p
                    className="min-w-0 font-mono text-[clamp(1.25rem,7.5vw,2.75rem)] font-bold tracking-[0.08em] text-primary-800 sm:tracking-[0.15em] dark:text-white"
                    aria-label={`Pairing code: ${code.split('').join(' ')}`}
                  >
                    {code}
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-primary-700 transition-colors hover:bg-primary-100 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none dark:text-primary-100 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
                    aria-label={copied ? t('pairing.generate.copied') : t('pairing.generate.copyAria')}
                    tabIndex={0}
                  >
                    {copied ? <PairingCopyCheckIcon /> : <PairingCopyClipboardIcon />}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCopyInviteLink}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900 transition-colors hover:bg-primary-100 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none dark:border-primary-500 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900"
                aria-label={t('pairing.generate.copyInviteLinkAria')}
                tabIndex={0}
              >
                {linkCopied ? (
                  <>
                    {t('pairing.generate.copied')}
                    <PairingCopyCheckIcon />
                  </>
                ) : (
                  <>
                    {t('pairing.generate.copyInviteLink')}
                    <PairingCopyClipboardIcon />
                  </>
                )}
              </button>

              <PairingInviteQrCode code={code} />

              <p className="mt-3 text-center text-sm font-bold text-primary-800 dark:text-primary-300">
                {t('pairing.generate.validUntil', {
                  datetime: expiresAt,
                  duration: formatPairingExpiryDurationLabel(PAIRING_CODE_EXPIRY_MINUTES, t),
                })}
              </p>

              <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
                {t('pairing.generate.footer')}
              </p>

              {/* Action Buttons — hidden while code is still valid (no regenerate) */}
              {!codeStillValid ? (
                <div className="mt-6 flex gap-3">
                  <AppButton
                    type="button"
                    variant="secondary"
                    onClick={handleGenerateCode}
                    disabled={isLoading}
                    className="flex-1"
                    aria-label={t('pairing.generate.regenerateAria')}
                  >
                    {isLoading ? t('pairing.generate.regenerating') : t('pairing.generate.regenerate')}
                  </AppButton>
                  <AppButton
                    type="button"
                    variant="primary"
                    onClick={handleContinue}
                    className="flex-1"
                    aria-label={t('pairing.generate.continueAria')}
                  >
                    {t('pairing.generate.continue')}
                  </AppButton>
                </div>
              ) : null}
            </div>
          ) : isBootstrapping || isLoading ? (
            <div
              className="flex w-full flex-col items-center justify-center gap-3 py-8"
              role="status"
              aria-live="polite"
              aria-label={t('pairing.generate.regenerating')}
            >
              <svg className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pairing.generate.regenerating')}</p>
            </div>
          ) : (
            <AppButton
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleGenerateCode}
              disabled={isLoading}
              aria-label={t('pairing.generate.generateAria')}
              className="px-6"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('pairing.generate.regenerating')}
                </span>
              ) : (
                t('pairing.generate.generateCta')
              )}
            </AppButton>
          )}

        </div>
      </div>
    </div>
  );

  return showMainShell ? <MainAppShell>{content}</MainAppShell> : content;
};
